import { vec2, vec3 } from 'gl-matrix';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ToolManager } from '~/engine/tool/ToolManager';
import { AdaptiveBrushTool } from '~/engine/tool/tools/contouring-tools/AdaptiveBrushTool';
import { StaticBrushTool } from '~/engine/tool/tools/contouring-tools/StaticBrushTool';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { brushOptionsState } from '~/state/atoms/toolsAtoms';
import { imageSlicesCountState } from '~/state/atoms/viewerAtoms';
import ViewportOverlay from './ViewportOverlay';
import usePlaneIntersectionPointChanged from '~/hooks/viewer/usePlaneIntersectionPointChanged';

export type ImageSliceIdxProps = {
    [key: string]: number;
};

export type ViewIndicesProps = {
    [key: string]: vec2 | null;
};

export type PatientSpaceCoordsProps = {
    coords: vec3 | null;
    canvasCoords?: vec2 | null;
    orthoCoords?: vec3 | null;
};

export type ImageSlicePositions = {
    [key in Orientations]: string;
};

interface ViewportProps {
    view: PlaneView | null;
    viewportOrientation: Orientations;
    canvas: React.RefObject<HTMLCanvasElement | null>;
    canvas2D: React.RefObject<HTMLCanvasElement | null>;
}

const Viewport = ({
    view,
    viewportOrientation,
    canvas,
    canvas2D,
}: ViewportProps) => {
    usePlaneIntersectionPointChanged(view);
    const [isActive, setIsActive] = useState(false);

    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const patientRepository = useRecoilValue(patientRepositoryState);

    const { environment } = useViewportEnvironment();

    const viewManager = useRecoilValue(viewManagerState);

    const [selectedROI] = useRecoilState(selectedROIState);
    const [brushOptions] = useRecoilState(brushOptionsState);

    const setImageSlicesCount = useSetRecoilState(imageSlicesCountState);

    const updateBrushDiameter = useCallback(() => {
        if (!viewManager || !environment || !environment.image) {
            return;
        }
        if (ToolManager.getActiveTool() instanceof StaticBrushTool) {
            (ToolManager.getActiveTool() as StaticBrushTool).diameter =
                brushOptions.diameter;
        } else if (ToolManager.getActiveTool() instanceof AdaptiveBrushTool) {
            (ToolManager.getActiveTool() as AdaptiveBrushTool).diameter =
                brushOptions.diameter;
        }
    }, [brushOptions.diameter, viewManager, environment]);

    const setSelectedPatient = useCallback(() => {
        if (patientRepository) {
            const patients = patientRepository.getAll();
            const patient = patients.find(
                (p) => p.dataSet.PatientID === selectedPatientId
            );
            const patientCTSeries = patient?.dataSet?.series.find(
                (series) => series.seriesModule.modality === 'CT'
            );
            if (patientCTSeries) {
                const numberOfImgSlices = patientCTSeries.numberOfObjects;
                setImageSlicesCount(numberOfImgSlices - 1);
            }
        }
    }, [patientRepository, selectedPatientId, setImageSlicesCount]);

    useEffect(() => {
        if (environment) {
            environment.setSelectedRoiByNumber(selectedROI);
        }
    }, [environment, selectedROI]);

    useEffect(() => {
        setSelectedPatient();
    }, [selectedPatientId, setSelectedPatient]);

    useEffect(() => {
        updateBrushDiameter();
    }, [brushOptions.diameter, updateBrushDiameter]);

    return (
        <div
            className={`viewport-container ${
                isActive ? 'active-viewport' : ''
            }`}
            onMouseEnter={() => setIsActive(true)}
            onMouseLeave={() => setIsActive(false)}
            onMouseMove={() => {
                if (!isActive) {
                    setIsActive(true);
                }
            }}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
            }}
        >
            <ViewportOverlay
                view={view}
                viewportOrientation={viewportOrientation}
                canvas={canvas}
                canvas2D={canvas2D}
                isActive={isActive}
            />
        </div>
    );
};

export default Viewport;
